import i18n from '@/plugins/vue-i18n';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useClusteringStore } from '@/stores/clustering';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import to from 'await-to-js';
import axios from 'axios';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { get, map, some, find, reduce } from 'lodash';
import ClusterSchemeTypes from '@enums/cluster-schemes-source';
import Classifications from '@enums/region-classifications';

export const useProductRegionsStore = defineStore('product-regions', () => {
  const scenariosStore = useScenariosStore();
  const clusteringStore = useClusteringStore();
  const scenarioProductsStore = useScenarioProductsStore();
  const snackbarStore = useSnackbarStore();
  // State
  const regions = ref([]);
  const loading = shallowRef(false);

  // Actions
  async function fetchRegions() {
    loading.value = true;
    const scenarioId = scenariosStore.selectedScenario._id;
    const [err, res] = await to(axios.get(`/api/scenarios/${scenarioId}/product-regions`));
    if (err) throw new Error(err.message);
    regions.value = res.data;
    loading.value = false;
  }

  async function generateDefaultRegionalClusterScheme() {
    // Do not generate cluster scheme if there are no regional classifications
    if (!some(regions.value, r => r.classification === Classifications.regional)) return;

    const scenarioId = scenariosStore.selectedScenario._id;
    const selectedAttributes = clusteringStore.getScenarioAttributes;
    // Cluster schemes are always deleted after generating regions so this will always be the first scheme
    const defaultClusterScheme = {
      name: i18n.t('clusteringPage.dialog.regionalScheme'),
      clusterCount: 1, // Single cluster allowed here as it will be subclustered by region
      scenarioId,
      source: ClusterSchemeTypes.regional,
      clusters: [
        {
          clusterName: i18n.t('clusteringPage.unassignedStores'),
          storeKeys: map(scenariosStore.stores, 'storeKey'),
        },
      ],
    };
    clusteringStore.saveClusteringSchemes({
      clusterSchemes: [defaultClusterScheme],
      selectedAttributes,
    });
  }

  async function updateScenarioProducts() {
    const selectedScenario = scenariosStore.selectedScenario;
    const selectedAttribute = get(selectedScenario, 'regionSettings.attribute');
    const customAttributes = get(selectedScenario, 'customAttributes', []);

    // Need the custom attributeId to query scenario products
    const attributeId = get(
      find(customAttributes, a => a.name === selectedAttribute),
      'id'
    );
    // Find hyperlocal attribute values
    const attributeValues = reduce(
      regions.value,
      (acc, r) => {
        if (r.classification === Classifications.hyperlocal) acc.push(r.attributeValue);
        return acc;
      },
      []
    );

    scenarioProductsStore.updateScenarioProductRegionClassifications({
      attributeId,
      attributeValues,
    });
  }

  async function finishGenerateRegionsByAttributeJob({ message, translation, color }) {
    const [scenarioErr, scenarioRes] = await to(axios.get(`/api/scenarios/${message.scenarioId}`));
    if (scenarioErr) throw new Error(scenarioErr.message);

    const scenario = scenarioRes.data.data;
    const scenarioIdFromEvent = get(message, 'scenarioId', null);
    const scenarioId = scenariosStore.selectedScenario._id;

    const content = i18n.t(translation, { name: scenario.name });
    snackbarStore.showSnackbar({ content, color });

    // do not refresh store in case user navigated away from current scenario
    if (scenarioIdFromEvent === scenarioId) {
      fetchRegions();
      scenariosStore.loadScenario(scenarioId);
    }
  }

  async function generateRegionsByAttributeJobComplete({ message }) {
    scenariosStore.setScenarioHasSkippedRegionsByAttribute({ hasSkippedRegionsByAttribute: false });
    finishGenerateRegionsByAttributeJob({
      message,
      translation: 'notifications.region.finished',
      color: 'success',
    });
    // Update hyperlocal products. These products will not be included in the assortment
    updateScenarioProducts();
    // Automatically create a cluster scheme if regional data has been generated
    generateDefaultRegionalClusterScheme();
  }

  function generateRegionsByAttributeJobFailed({ message }) {
    finishGenerateRegionsByAttributeJob({
      message,
      translation: 'notifications.region.failed',
      color: 'error',
    });
  }

  function setLoading(value) {
    loading.value = value;
  }

  async function updateRegions(updates) {
    loading.value = true;
    const scenarioId = scenariosStore.selectedScenario._id;
    const [err] = await to(
      axios.patch(`/api/scenarios/${scenarioId}/product-regions`, { updates })
    );
    if (err) throw new Error(err.message);

    fetchRegions();
    loading.value = false;
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));

    // Update hyperlocal products. These products will not be included in the assortment
    updateScenarioProducts();
    // Automatically generate a new cluster scheme based on updated region data
    generateDefaultRegionalClusterScheme();
  }

  return {
    // State
    regions,
    loading,
    // Actions
    fetchRegions,
    setLoading,
    generateDefaultRegionalClusterScheme,
    updateScenarioProducts,
    finishGenerateRegionsByAttributeJob,
    generateRegionsByAttributeJobComplete,
    generateRegionsByAttributeJobFailed,
    updateRegions,
  };
});
